import React from 'react';
import styled from 'styled-components';
import {
    Button,
    Typography,
    VerticalSpacer,
    SectionInnerGridContent,
    GridContainer,
    EyebrowProps,
    Image,
    ScrollIndicator
} from '../../elements';
import {AnimateOnViewWrap, Spotlight} from '../../wraps';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {useUnrealUIContext} from '../../../shared/context/componentContext';
import {SocialIconsStacked} from '../../elements/icons/socials/SocialIconsStacked';

const Styled = styled.div`
    position: relative;
    background-color: ${(p) => p.theme.palette.background.default};
    min-height: 65vh;

    .image-wrapper:before {
        content: '';
        position: absolute;
        background: linear-gradient(
            0deg,
            ${(p) => p.theme.palette.background.default},
            rgba(13, 16, 35, 0) 100%
        );
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
    }

    .image-wrapper {
        position: absolute;
        height: 65vh;
    }

    &.thin.has-image {
        .hero-copy {
            padding-top: 280px;
        }
    }

    &.thin.no-image {
        .hero-copy {
            padding-top: 128px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                padding-top: 96px;
            }
        }
    }

    .hero-copy {
        position: relative;
        color: ${(p) => p.theme.palette.text.primary};
        padding-top: 35vh;
        width: 100%;
    }

    .buttons-container {
        display: flex;
        justify-content: center;
        flex-flow: row nowrap;
        ${(p) => p.theme.breakpoints.down('sm')} {
            flex-flow: row wrap;
        }
        & > span {
            padding-left: ${(p) => p.theme.spacing(2)}px;
            padding-right: ${(p) => p.theme.spacing(2)}px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                padding-top: ${(p) => p.theme.spacing(2)}px;
            }
        }
    }

    .attribution-caption .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
        font-size: 10px;
        line-height: 16px;
        letter-spacing: 0.2px;
    }

    .social-icons {
        position: absolute;
        height: 100%;
        top: 0;
        right: 0;
        width: 96px;
        .social-icons-inner {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 100%;
        }
    }
`;

export interface ImageHeroProps {
    image?: string;
    eyebrow?: EyebrowProps;
    title?: string;
    description?: string;
    smallDescription?: string;
    href?: string;
    secondaryHref?: string;
    caption?: string;
    buttonText?: string;
    secondaryButtonText?: string;
    accentColor?: AccentColorOptions;
    alt?: string;
    minAnimateDelay?: number;
    showScrollIndicator?: boolean;
    showSocialIcons?: boolean;
    _type?: string;
    thin?: boolean;
}

export const ImageHero = ({
    image,
    eyebrow,
    title,
    smallDescription,
    description,
    href,
    secondaryHref,
    caption,
    buttonText,
    secondaryButtonText,
    accentColor,
    alt,
    minAnimateDelay = 0,
    showScrollIndicator = false,
    showSocialIcons,
    thin
}: ImageHeroProps): JSX.Element | null => {
    const {imageHero} = useUnrealUIContext();
    const isThinVariant = thin || imageHero?.spacing === 'thin';
    const validImage = Boolean(image);
    const isSm = useBreakpointDown('sm');

    const textSpacer = isThinVariant ? (
        isSm ? (
            <VerticalSpacer variant="eyebrow" />
        ) : (
            <VerticalSpacer variant="custom" spacing={3} />
        )
    ) : (
        <VerticalSpacer variant="heading" />
    );

    return (
        <Styled
            className={`ue-image-hero ${isThinVariant ? 'thin' : ''} ${
                validImage ? 'has-image' : 'no-image'
            }`}>
            {image ? <Image disableRatioHelper src={image} alt={alt} /> : ''}
            <div className="hero-copy">
                <GridContainer>
                    <SectionInnerGridContent>
                        {eyebrow && eyebrow.text && (
                            <>
                                <AnimateOnViewWrap delay={minAnimateDelay} translateY={20}>
                                    <Typography variant="h4" big>
                                        {eyebrow.text}
                                    </Typography>
                                </AnimateOnViewWrap>
                                {textSpacer}
                            </>
                        )}
                        {title && (
                            <>
                                {!eyebrow && <VerticalSpacer variant="heading" />}
                                <AnimateOnViewWrap delay={minAnimateDelay + 0.1} translateY={15}>
                                    {!validImage ? (
                                        <Spotlight
                                            accentColor={accentColor}
                                            variant="small"
                                            opacity={0.7}
                                            offsetRatio={[-0.3, -3.5]}
                                            offsetRatioDown={[-0.3, -3.5]}>
                                            <Typography variant="h1">{title}</Typography>
                                        </Spotlight>
                                    ) : (
                                        <Typography variant="h1">{title}</Typography>
                                    )}
                                </AnimateOnViewWrap>
                            </>
                        )}
                        {description && (
                            <>
                                {textSpacer}
                                <AnimateOnViewWrap delay={minAnimateDelay + 0.2} translateY={10}>
                                    <Typography variant="h2" color="secondary">
                                        {description}
                                    </Typography>
                                </AnimateOnViewWrap>
                            </>
                        )}
                        {smallDescription && (
                            <>
                                {textSpacer}
                                <AnimateOnViewWrap delay={minAnimateDelay + 0.225} translateY={10}>
                                    <Typography variant="h3" color="secondary">
                                        {smallDescription}
                                    </Typography>
                                </AnimateOnViewWrap>
                            </>
                        )}
                        {caption && (
                            <div className="attribution-caption">
                                {textSpacer}
                                <AnimateOnViewWrap delay={minAnimateDelay + 0.25} translateY={10}>
                                    <Typography variant="p" caption>
                                        {caption}
                                    </Typography>
                                </AnimateOnViewWrap>
                            </div>
                        )}
                        {(href && buttonText) || (secondaryHref && secondaryButtonText) ? (
                            <>
                                {isThinVariant ? (
                                    isSm ? (
                                        <VerticalSpacer variant="custom" spacing={4} />
                                    ) : (
                                        <VerticalSpacer variant="custom" spacing={5} />
                                    )
                                ) : (
                                    <VerticalSpacer variant="heading" />
                                )}
                                <AnimateOnViewWrap delay={minAnimateDelay + 0.25} translateY={10}>
                                    <div className="buttons-container">
                                        {href && buttonText ? (
                                            <Button type="primary" href={href} color={accentColor}>
                                                {buttonText ? buttonText : 'Learn more'}
                                            </Button>
                                        ) : (
                                            ''
                                        )}
                                        {secondaryHref && secondaryButtonText ? (
                                            <Button
                                                type="secondary"
                                                href={secondaryHref}
                                                color={accentColor}>
                                                {secondaryButtonText}
                                            </Button>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </AnimateOnViewWrap>
                            </>
                        ) : (
                            ''
                        )}

                        {isThinVariant ? (
                            isSm ? (
                                <VerticalSpacer variant="custom" spacing={5} />
                            ) : (
                                <VerticalSpacer variant="custom" spacing={6} />
                            )
                        ) : (
                            <VerticalSpacer variant="section" />
                        )}
                        {showScrollIndicator && <ScrollIndicator />}
                    </SectionInnerGridContent>
                </GridContainer>
            </div>
            {showSocialIcons && !isSm && (
                <div className="social-icons">
                    <div className="social-icons-inner">
                        <SocialIconsStacked accentColor={accentColor} />
                    </div>
                </div>
            )}
        </Styled>
    );
};
